import NotamLayer from '../map/notam';
import RouteLayer from '../map/route';
import {allAirportRef} from '@/firebase/airport';
import {useAppDispatch, useAppSelector} from '@/hooks/redux';
import {setSelectedRadar} from '@/redux/slice/emissionSlice';
import {Track} from '@/types/radar';
import {airportGetColor} from '@/utils/color';
import {fixLineString} from '@/utils/map';
import {getPointAtPercentage} from '@/utils/route';
import {getAirportCode} from '@/utils/string';
import * as turf from '@turf/turf';
import {get} from 'firebase/database';
import mapboxgl from 'mapbox-gl';
import {useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';

let hoveredAircraftId: string | null = null;
let selectedAircraftId: string | null = null;
let popupSelected: any = null;
let popupCurrentCII: any = null;
const sectorPopup: any = {};
let geoData: any = [];

const popupAvgColor = (value: number) => {
  const temp = value.toFixed(3);
  if (temp === '0.000') return 'gray';
  if (value > 0) return 'orange';
  return 'green';
};

const ATCMap = () => {
  const [isMapReady, setIsMapReady] = useState(false);
  const dispatch = useAppDispatch();
  const sector = useAppSelector((state) => state.emission.sector);
  const radars = useAppSelector((state) => state.emission.radars);
  const selectedRadar = useAppSelector((state) => state.emission.selectedRadar);
  const flightCII = useAppSelector((state) => state.emission.flightCII);
  const activeTab = useLocation().pathname;
  const isLiveEmission = useMemo(() => {
    return activeTab === '/atc';
  }, [activeTab]);

  useEffect(() => {
    const fetchAirportData = async () => {
      await get(allAirportRef()).then((snapshot) => {
        const data = snapshot.val();
        // map object to array
        const arr = Object.keys(data).map((key) => ({
          ...data[key],
          iata: getAirportCode(data[key].airport)
        }));
        geoData = arr.map((item) => ({
          type: 'Feature',
          properties: {
            type: 'airport',
            id: item.airport,
            colour: window?.airlineStation?.includes(item.airport)
              ? 'sia'
              : item.colour,
            size: window?.airlineStation?.includes(item.airport) ? 0.4 : 1,
            textColor: airportGetColor(item.colour)
          },
          geometry: {
            type: 'Point',
            coordinates: [parseFloat(item.long), parseFloat(item.lat)]
          }
        }));
        const bound = window.map.getBounds();
        const mapNodes = geoData.filter((item: any) => {
          const lat = item?.geometry?.coordinates[1];
          const lng = item?.geometry?.coordinates[0];
          return (
            lat >= (bound?._sw?.lat ?? 0) &&
            lat <= (bound?._ne?.lat ?? 0) &&
            lng >= (bound?._sw?.lng ?? 0) &&
            lng <= (bound?._ne?.lng ?? 0)
          );
        });
        window.updateData({
          event: 'airport',
          data: mapNodes
        });
      });
    };

    void fetchAirportData();
  }, []);

  const clearSelectTrack = () => {
    if (popupSelected) {
      popupSelected.remove();
    }
    if (popupCurrentCII) {
      popupCurrentCII.remove();
    }
    Object.keys(window.popupProjectedCII).forEach((date) => {
      if (window.popupProjectedCII[date]) {
        window.popupProjectedCII[date].remove();
        window.popupProjectedCII[date] = undefined;
      }
    });
    try {
      // select all element with class name popup-atc-current or popup-atc-projected and remove
      const popupAtcCurrent = document.querySelectorAll('.popup-atc-current');
      const popupAtcProjected = document.querySelectorAll(
        '.popup-atc-projected'
      );
      popupAtcCurrent.forEach((item) => item.remove());
      popupAtcProjected.forEach((item) => item.remove());
    } catch {}
    window.updateData({
      event: 'trafficSelected',
      data: []
    });
    window.updateData({
      event: 'currentTrack',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });
    window.updateData({
      event: 'currentTrack',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });
    window.updateData({
      event: 'firstTrack',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });
    window.updateData({
      event: 'secondTrack',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });
    window.updateData({
      event: 'thirdTrack',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });
  };

  useEffect(() => {
    if (!isMapReady) return;
    Object.keys(sectorPopup).forEach((key) => {
      sectorPopup[key].remove();
    });
    if (!sector) {
      window.updateData({
        event: 'sector',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
      window.updateData({
        event: 'sectorRoute',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
      return;
    }
    const sectors = sector.split('-');
    const dep = sectors[0];
    const arr = sectors[1];
    const depInfo = window.airports?.find((i) => i.code === dep);
    const arrInfo = window.airports?.find((i) => i.code === arr);
    if (!depInfo || !arrInfo) return;
    const location = depInfo.location
      .replace('(', '')
      .replace(')', '')
      .split(' ')
      .map(Number);
    const arrLocation = arrInfo.location
      .replace('(', '')
      .replace(')', '')
      .split(' ')
      .map(Number);
    const sectorData: any = {
      type: 'FeatureCollection',
      features: []
    };
    sectorData.features.push({
      type: 'Feature',
      properties: {
        id: dep,
        type: 'departure',
        icon: '●'
      },
      geometry: {
        type: 'Point',
        coordinates: [location[0], location[1]]
      }
    });
    sectorPopup[dep] = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 5,
      anchor: 'bottom'
    });
    sectorPopup[dep]
      .addClassName('popup-atc')
      .setLngLat([location[0], location[1]])
      .setHTML(`Dep: ${depInfo.code}`)
      .addTo(window.map);
    sectorData.features.push({
      type: 'Feature',
      properties: {
        id: arr,
        type: 'arrival',
        icon: '■'
      },
      geometry: {
        type: 'Point',
        coordinates: [arrLocation[0], arrLocation[1]]
      }
    });
    sectorPopup[arr] = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 5,
      anchor: 'bottom'
    });
    sectorPopup[arr]
      .addClassName('popup-atc')
      .setLngLat([arrLocation[0], arrLocation[1]])
      .setHTML(`Arr: ${arrInfo.code}`)
      .addTo(window.map);
    window.updateData({
      event: 'sector',
      data: sectorData
    });
    // make a curved line with turf
    const start = location;
    const end = arrLocation;
    const distance = turf.distance(start, end, {units: 'miles'});
    let lines = [location, arrLocation];
    if (distance < 3000) {
      const midpoint = turf.midpoint(start, end);
      const bearing = turf.bearing(start, end);
      const leftSideArc =
        bearing - 90 < -180 ? 180 + (bearing - 90 + 180) : bearing - 90;
      const destination = turf.destination(
        midpoint,
        distance / 10,
        leftSideArc,
        {
          units: 'miles'
        }
      );
      const curvedLine = turf.bezierSpline(
        turf.lineString([start, destination.geometry.coordinates, end])
      );
      lines = fixLineString(curvedLine.geometry.coordinates as any);
    }

    // curvedLine gets rendered to the page

    window.updateData({
      event: 'sectorRoute',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: fixLineString(lines as any)
        }
      }
    });
    window.map.fitBounds(
      [
        [location[0], location[1]],
        [arrLocation[0], arrLocation[1]]
      ],
      {
        padding: {
          top: 100,
          bottom: 100,
          left: 100,
          right: 300
        }
      }
    );
  }, [sector, isMapReady]);

  useEffect(() => {
    if (!isMapReady) return;
    if (isLiveEmission) {
      window.updateData({
        event: 'toggleTraffic',
        data: 'visible'
      });
    } else {
      window.updateData({
        event: 'toggleTraffic',
        data: 'none'
      });
    }
  }, [isLiveEmission, isMapReady]);

  useEffect(() => {
    if (!isMapReady || !selectedRadar) return;
    if (flightCII) {
      popupCurrentCII = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 5,
        anchor: 'bottom'
      });
      const potentialEPI = parseFloat(
        (flightCII.current_cii - flightCII.baseline_cii).toFixed(3)
      );
      const color = popupAvgColor(potentialEPI);
      let value = `${potentialEPI < 0 ? -1 * potentialEPI : potentialEPI} ${
        potentialEPI > 0 ? '>' : '<'
      } average`;
      if (color === 'gray') {
        value = 'Average EPI';
      }
      const popupSelectedLocation = popupSelected?.getLngLat();
      const currentLocation = popupSelectedLocation
        ? [popupSelectedLocation.lng, popupSelectedLocation.lat]
        : [selectedRadar.long, selectedRadar.lat];
      popupCurrentCII
        .setLngLat(currentLocation)
        .addClassName('popup-atc-current')
        .addClassName('blue')
        .setHTML(
          `<div class="atc-potential-title">Current Route</div>
<div class="atc-potential-title">Projected EPI</div>
<div class="atc-potential-value">${flightCII.current_cii.toFixed(3)}</div>
<div class="atc-potential-box ${color}">
<span class="text">${value}</span></div>`
        )
        .addTo(window.map);
      const lastTracks = flightCII?.last_3_tracks ?? {};
      const dates = Object.keys(lastTracks);
      if (dates.length > 0) {
        dates.forEach((date, index) => {
          const projectedTrack = lastTracks[date];
          const projectedTrackPoints = projectedTrack?.track;
          // find the nearest point to currentLocation
          let centerPointOfTrack = projectedTrackPoints.reduce(
            (prev: Track, curr: Track) => {
              return Math.abs(curr.latitude - currentLocation[1]) +
                Math.abs(curr.longitude - currentLocation[0]) <
                Math.abs(prev.latitude - currentLocation[1]) +
                  Math.abs(prev.longitude - currentLocation[0])
                ? curr
                : prev;
            },
            projectedTrackPoints[0]
          );
          const distance = turf.distance(
            turf.point([
              centerPointOfTrack.longitude,
              centerPointOfTrack.latitude
            ]),
            turf.point(currentLocation)
          );
          if (distance < 100) {
            const currentIndex = projectedTrackPoints.findIndex(
              (point) =>
                point.longitude === centerPointOfTrack.longitude &&
                point.latitude === centerPointOfTrack.latitude
            );
            const distanceMap: any = {
              0: 30,
              1: 40,
              2: 60
            };
            // get point move ahead 20 miles
            let nextPoint = null;
            for (
              let i = currentIndex + 1;
              i < projectedTrackPoints.length;
              i++
            ) {
              const point = projectedTrackPoints[i];
              const distance = turf.distance(
                turf.point(currentLocation),
                turf.point([point.longitude, point.latitude])
              );
              if (distance >= distanceMap[index]) {
                nextPoint = point;
                break;
              }
            }
            if (!nextPoint) {
              nextPoint = projectedTrackPoints[projectedTrackPoints.length - 1];
            }
            centerPointOfTrack = nextPoint;
          }
          let tracks: any = false;
          const trackName: any = {
            0: 'firstTrack',
            1: 'secondTrack',
            2: 'thirdTrack'
          };
          const trackClassName: any = {
            0: 'first',
            1: 'second',
            2: 'third'
          };
          if (projectedTrackPoints && Array.isArray(projectedTrackPoints)) {
            tracks = {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'LineString',
                coordinates: fixLineString(
                  projectedTrackPoints.map((track) => [
                    track.longitude,
                    track.latitude
                  ])
                )
              }
            };

            window.updateData({
              event: trackName[index],
              data: tracks
            });
          }
          const popup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            offset: 0,
            anchor: 'bottom'
          });
          const potentialEPI = parseFloat(
            (projectedTrack.cii - flightCII.baseline_cii).toFixed(3)
          );
          if (centerPointOfTrack) {
            const color = popupAvgColor(potentialEPI);
            let value = `${
              potentialEPI < 0 ? -1 * potentialEPI : potentialEPI
            } ${potentialEPI > 0 ? '>' : '<'} average`;
            if (color === 'gray') {
              value = 'Average EPI';
            }
            popup
              .addClassName('popup-atc-projected')
              .addClassName(trackClassName[index])
              .setLngLat([
                centerPointOfTrack?.longitude,
                centerPointOfTrack?.latitude
              ])
              .setHTML(
                `<div class="atc-potential-title">Alternate Route</div>
	<div class="atc-potential-title">Projected EPI</div>
	<div class="atc-potential-value">${projectedTrack.cii.toFixed(3)}</div>
	<div class="atc-potential-box ${popupAvgColor(potentialEPI)}">
	<span class="text">${value}</span></div>`
              )
              .addTo(window.map);
            window.popupProjectedCII[date] = popup;
          }
        });
      }
    } else {
      clearSelectTrack();
    }
  }, [flightCII, selectedRadar, isMapReady]);

  useEffect(() => {
    if (!isMapReady) return;
    clearSelectTrack();
    if (!selectedRadar) {
      window.map.setPaintProperty('traffic-symbol', 'icon-opacity', 0.5);
    } else {
      window.map.setPaintProperty('traffic-symbol', 'icon-opacity', 0.1);
      const coordinates = [selectedRadar.long, selectedRadar.lat];
      if (popupSelected) {
        popupSelected
          .setLngLat(coordinates)
          .addClassName('popup-atc')
          .setText(selectedRadar.callsign)
          .addTo(window.map);
      }
      selectedAircraftId = selectedRadar.callsign;
      window.updateData({
        event: 'camera',
        data: {
          center: coordinates,
          zoom: 6
        }
      });
      window.updateData({
        event: 'trafficSelected',
        data: [
          {
            type: 'Feature',
            properties: {
              ...selectedRadar,
              track: null,
              opacity: 1
            },
            geometry: {
              type: 'Point',
              coordinates: [selectedRadar.long, selectedRadar.lat]
            }
          }
        ]
      });
      let tracks: any = false;
      if (
        window.atcTrack[selectedRadar.callsign] &&
        window.atcTrack[selectedRadar.callsign] !== '' &&
        Array.isArray(window.atcTrack[selectedRadar.callsign])
      ) {
        tracks = {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: fixLineString(
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              window.atcTrack[selectedRadar.callsign].map((track: any) => [
                track.long,
                track.lat
              ])
            )
          }
        };
      }
      window.updateData({
        event: 'currentTrack',
        data: tracks
      });
    }
  }, [selectedRadar, isMapReady]);

  useEffect(() => {
    if (!isMapReady) return;
    if (!radars) return;
    window.updateData({
      event: 'traffic',
      data: radars.map((radar) => ({
        type: 'Feature',
        properties: {
          id: radar.callsign,
          heading: radar.trueTrack,
          ...radar,
          track: null,
          opacity: 1
        },
        geometry: {
          type: 'Point',
          coordinates: [radar.long, radar.lat]
        }
      }))
    });
  }, [radars, isMapReady]);

  useEffect(() => {
    mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_KEY;
    window.map = new mapboxgl.Map({
      container: 'map',
      style: import.meta.env.VITE_MAPBOX_STYLE,
      center: [103.806241, 1.3246091],
      minZoom: 2.5,
      zoom: 5
    });
    const airportImages = [
      'airport-icon-green',
      'airport-icon-amber',
      'airport-icon-red',
      'airport-icon-green-fill',
      'airport-icon-amber-fill',
      'airport-icon-red-fill'
    ];
    airportImages.forEach((item) => {
      const size = item.includes('fill') ? 12 : 5;
      const img = new Image(size, size);
      let xml = `<svg
        id="airport-icon-green"
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clip-path="url(#clip0_5429_70058)">
          <path
            d="M1 5C1 2.792 2.792 1 5 1C7.208 1 9 2.792 9 5C9 7.208 7.208 9 5 9C2.792 9 1 7.208 1 5Z"
            stroke="#1DB954"
            stroke-width="1.5"
            fill="transparent"
          />
        </g>
      </svg>`;
      if (item.includes('green')) {
        xml = xml.replace('transparent', '#1DB954');
      }
      if (item.includes('amber')) {
        xml = xml
          .replace('#1DB954', '#FF9500')
          .replace('transparent', '#FF9500');
      }
      if (item.includes('red')) {
        xml = xml
          .replace('#1DB954', '#F04438')
          .replace('transparent', '#FF9500');
      }
      if (item.includes('green-fill')) {
        xml = xml.replace('transparent', '#1DB954');
      }
      if (item.includes('amber-fill')) {
        xml = xml.replace('transparent', '#FF9500');
      }
      if (item.includes('red-fill')) {
        xml = xml.replace('transparent', '#F04438');
      }
      img.onload = () => window.map.addImage(item, img);
      img.src = `data:image/svg+xml;base64,${btoa(xml)}`;
    });
    // window.map.setProjection('equalEarth');
    const iconFromInternet: any = {
      'aircraft-icon': '/images/atc/aircraft.png',
      'aircraft-hover-icon': '/images/atc/aircraft-hover.png',
      'aircraft-focus-icon': '/images/atc/aircraft-focus.png'
    };
    Object.keys(iconFromInternet).forEach((key) => {
      window.map.loadImage(iconFromInternet[key], (error, image: any) => {
        if (error) throw error;
        window.map.addImage(key, image);
      });
    });
    const base64IconFromInternet: any = {
      fir: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAACXBIWXMAABYlAAAWJQFJUiTwAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAIDSURBVHgB7ZrNTcMwGIbfz0kluHEBisRP2QAmABYAMgEwAWWC0gkoE1AmCIgBYASYgIgfqXDqCQ5tbZwiIUhKaycODpKfWy2n+Z7Eb6R8DuCwC6EAauH9zLtX2QfExudJxGVne7mNAjAuMBc+7BJjJ7L4WuJUERFvmhYxJlC9etwUAg0IbI6fSZHPETwHi7cwQG6BanhfE55/NrnwnwiiNht4zU6wECEHmQXidf7m+4fEcYwc5BXRFvhWeF3+nIERKAINTl+2V1rQREvg94CaQj/oSgLqATWFetDHCmQNqClU8jFSwFRATTFOJCUwvOqscl3cOs+KzAf3tpISLDmNe5VG+YqPEfLC9uvJ0ZQACbGL8rKXHGAjJhl6thdCqjaGf44TsI0TsI0TsI0TsI0TsI0TsI0TsI0TsM0ogS7KS6q2UQLnKClEdJEcS7/U835r2KssHbIm2RtKjqYEOsGq7L/0tuSuSmnuhGywNad5b12psfWduMkF5h0L0B4sQIQbDPwD7dZiktnwaY0xhH/V8PosHLKVuHQzcS40qIYP+4KxRpHtdcH50WuwfKF8BDIwHz7VZXoODYp05To/nUK/FQWrWo/xzFtMpvIRBzRL4V/HIydZRVQCqvQ/MIRq0HUCqoLxje7fg64fUBUK+dQgZiji0Q6BugK4mx702lnXuaPMfABQeNhAfXD9HQAAAABJRU5ErkJggg==',
      'fir-notam-icon':
        'data:image/png;base64,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',
      'notam-icon':
        'data:image/png;base64,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',
      'pirep-icon':
        'data:image/png;base64,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',
      'notam-bookmarked-icon':
        'data:image/png;base64,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',
      'fir-notam-bookmarked-icon':
        'data:image/png;base64,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',
      'fir-icon':
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGAAAABgCAYAAADimHc4AAAACXBIWXMAACxLAAAsSwGlPZapAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAOVSURBVHgB7Z1BTttAFIbfs03V7li1VGppuAE3KCdAyglKTwCcIOQG9ASEE2TBAUhvwA2Img3ssqqySGZqB6VVI8Q8T8Z+M+H/doATS/M5/oxAb4gAAAC8VpgSpDO8350VxSFZ21l+g3n8dj6/G3cPppQYSQnYG953bJ6fkuWT8svd9Z9b5kG2yPsP3Y9jSoQkBFRX/O+iOGVDZ/TMwq9jM7rI5sV1CiKiF/DhZnJKhi5IsPD/w2Nm03843h9QxEQrYO9mcmQNX5XXc4c2Im4R0QlYLrylHlk6opAwjXhRfI/tthSNgCqwJt/psbUn1CCxhVpdQN3AhiKWUKsK8A9sKPT7oCIgXGBDoSeiVQGNBTYUCqFuRUBbgQ1Fm6FuVIBWYEPRRqgbE6Af2FA024fgAuILbCiaERFMQPSBDUXgUG8sILXAhiJUqL0FpB7YUGwaai8B2xPYUPj3oZaA7Q1sKEoRxpZ9+DySviKTHvj+ZtKzhm6x+C9hO+Ut6bZaK+krRJ+AveGvE5tVVz4Qw+b88fjLpfMw1wHLP4RnO7jy6zN9Z+YHrv/UENyC8iMsvhe7s6w4cx3kFGBz/kbAC8v01XWM+xNg6ZCAH4K1kzwF4VnfH+faiR9DQTNAgDIQoAwEKAMBykCAMhCgDAQoAwHKQIAyEKAMBCgDAcpAgDIQoAwEKAMBykCAMhCgDAQoAwHKQIAyEKAMBCgDAcpAgDIQoAwEKAMBykCAMhCgDAQoAwHKQIAyEKCMREByE8kjwrl2bgFMdwS8YMHaOQWwpZ8EPLHXriOcAmZmXs07wG2oNjymxc7IdZRTwLR7MGVjzwnUYjk/SDDESfQU9NDdH7ChPgER1VpJhzfVG9g0nBw9ja3B8I7nqT+wyWtk2dP8oKwHEX+ZkuH+Y/eTcz7QOt5D+6o5QkRFKYLE06G2kLKP9OMtzS99d3AKMraSsvzC0usaa8PMA9IcW7nOUkReXNktH9xa/nI1okUZ2Rr3+RffjwKzvX2oH1jRu1JDbJEI78BKaHR8feKh3jiwElrbwCGlUIcKrOhc1CKxhzp0YEXnJAXi60MzgRWdmRSJQESjgZWgvpGbUqhbCayEqLYybCPUbQZWQnybeTYUao3ASoh3O9tgfdALrIToN3TeQIR6YCUksaV5zVBHE1gJSQhY4Qh1udjmms2byxT2kl+RlIAV1Q5OMyoO/92WeFxe8XcpXPEAAADAij/wn8D9YwOebwAAAABJRU5ErkJggg==',
      'waypoint-icon':
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEwAAAA8CAYAAADMtVzqAAAACXBIWXMAACxLAAAsSwGlPZapAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAALlSURBVHgB7ZrNbdtAEIWHu9TBN11iE8iF7kDpQCU4HagDqQO6g5SQEojYyDklpAQ7koFcfXIChOBG6x/AoDkid7kzO6T8HXywfDA+LN8TyQfwzjTIy5s5CESBUB7SdP2xvFuAMEQKy8qbPKnhstLmCwhDpDCj0ydRBpbZ1XYFgkhAGFm5XRmVfH31q/uTujq//Xx+DwIQdcLspWiUKhq/nv/RugAhiBJmtF7vf+ZvP0g2WblbggDECLOny4rBPjcaRJwyMcKMSsvDfwDLs6tfG4iMiNBvCXqM6AUQ/YQhQY8RvQCiC6v1rGgNeozIBRBV2OM3emNW4EjMAogqzKjZD/AhYgFEE2aD3ulSbGJUEeOJRhRhjkGPMX/QM/ab8yjCnIMeweYfdwGwC/MNegyjgPWUsQvzDnqcRfZtdwlMsAo7u96tQ1yKTQzAOit/58AAm7DHm+s6ofoqMDe6Yrk02YSFCnoUAxccBcBy8/3h+91CVeYnkJPcntT/PlHenLOcMFVBCSyY/K9KSe8AyIWdXu9oL8UG1AVAKuz5dRn3PZ8tgD7P1rwgFfYU9MD/Btu+niMqALLQd3iKSgRNAZCdsAA310P/A5ICIBHGHfQYFAUQXNjLLgJkELwAggt7Dno57AvgtNxeQCCChn78oMcIVwBBT1j8oMcIVwDBhEkJeox9ARQhCiCIMGFBjxKiAIIIExf0GAEGeoNDX27QowzaZww6Yfa9oNygRxm0zxgkzC6dJQc9ikk2vgttb2FjCXoM34W2t7DOAZx0PPcZXqE/wqDHcC4A5xMWaBchBecCcBaGLp3HiuNAz0lY19J5rLgM9JyEEewiZOBQAL2FDR7ASafnQK+XsIkFPUavAugljHwXIYUeBdApLPQATjpdA71OYZMNepzFoQI4KGzyQY9xoABQYUcS9BjoQhsVdjRBj4AttFuF2QHcMQU9RtsDhlZhfAM46Zi8udB+I4xq6TxWOBfak+Q/7FdRGiitB/0AAAAASUVORK5CYII=',
      'aircraft-icon-dark':
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACoAAAAsCAYAAAATmipGAAAACXBIWXMAABYlAAAWJQFJUiTwAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAMQSURBVHgB7ZltSBNhHMD/t801tWyV70jdIF8is/XBKIq8QVHfXAR9KWnSp/pQFhR9iSlCYX5QA3sHT9KiT81P+SHaCUVageeHIJDwamSKlkvncHO7dc8xxZe93MszCnY/eO62u+cZv/3v/rf/8wwgXYlEImbUADMEYEKQI32dPV2BjyMUsTELQKejt951NhEEwQEGDICJ35eanKFRjhLfTIpbx1xnDynsbYABHWBAvNQ871h7PChEF9dtgEVUwBzx+eOeAwzgEk05mihuNFHcaKK40URxkz6iQfcHq/dGS1d4cnrdOXRs9s4j988TDhJUorjMQ8WG78kLZ+Dtp4ZYkivRF+QKLa/R3HK9Qyj7vKAARaJzT19SQfdgV2h8kpQzzlhdxZmOHW7KPLKfBpnIEkXF8Vzr47bAEGvn5/2gBCI7C/TF+bKLasmivge9lxcG2cbwxBSuaQa3uflKh6l6b7uUzklFUbLM979pC458oSAFmGwHOb/7va2on+YS9YsrKidZ1CIl2WKKKk0WtSRKtlWiOJJFLfGSbVlUbrJEP9CrK8zrzq6rZWdvtq976KNLmn3+dH1w+DMVHvPUhDwTZER6AFYlmyiKIvnLcW0s2b2I5DJ2bmcMleUDBmsFk7lnF5NoPBLdRrdaliIj9LPOPnzu4EfHakLTM1Zewg/F0vik83rxRi/MZXQ7SvpyLpxxqVlQEMaywq4hKk16b92jwt/Hz0UWAlQy6ZiihlLSqy/KZwlzDpKjlzPx4lnARfQL06iJufHMRcGPqdrFbx576KtnvdPyizKLN8NS4jKeOt5tqqxgUyEnUdrsf8VQwqqLnZ+eqVklGu24RTzSCP+UaIBc0QbQfFXcaYUzbjRR3KSPKHoG/rl9P+6cCZ1DfUAlqpbGpVZZ+uICboPtQP2mupMMKERxROd7+5yB1+/cUkrBsNAH9UVjQCGKIiq1iFnL2iJFDlj+bEBVlbGqnCUK8gaMh/a5DCEA/9CwXWqVJAXFoqg2MO4uYwylloFM+1E6xhSCQZuVBQe/uEiBxn/CXwkNkfgryYXvAAAAAElFTkSuQmCC',
      'aircraft-icon-medium':
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACoAAAAsCAYAAAATmipGAAAACXBIWXMAABYlAAAWJQFJUiTwAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAALUSURBVHgB7Znba9NQHMe/EUEQtlXHwA3FzLK9bRQ2HxRG00cnzILQl3lpH8XbmH9A21df6hQUfGkmDkRUqvOKSlMvL1ahIjrc2BYcCqKD2Cl4oRzPSVu33pP0DAfNB9LAubQffukv53cSoFEhhDjYgbUKlRPJk6Nxckkk5IqLkGt9UdYGTqwHL673B/H5lbSixY/EMZGePeDAOnBAv9SZjL+k48MdidffgIsoxYHfWuU+DvASXXVsUd7YoryxRXlji/KmcUTJ9FUXYp4o0mpp5xJte3ggTi5ARJ0IsIhebDw/FcTcjRFdqBpNItAihjAUHxMEQYMFLImSZEjC9EQU2oxoZh6271HRHQgL3T4ZJjElqhfCjw9FoE568ctSYIANtJhyOGXsfxmm0VWNTjMsSp6eOAn1dgjpOV7bDBWDd8eEzsEzRgbXFNWT5e35CD4pElaDrmEVsxMe4QgVr0JFUVPJUi8Gkq2sqOVkqZcqyVYgyiVZ6qVCsv0TNZ0s7AubnRqad4yjP5TCvb2lN312SXefDmDhkYTF125o70UTAShINl1Uj+Tlzvmyq0uxXKtLQYeUQIdbEbZ5lKrzmejB+c58ZOg4F56N+vHlhRvfP7oMLRS5+bX39Wxwk6igtecmBs7Gsj+qwAp0boqeRnLSIu77JGjvDuPPD6mWdHnRtj52SVPYuIXJydlMVGjHOfAiF2WZHbp0MiwhPbsPi2+8+JpCZdG2nRo298TQOzqOrVJq+TbBT86gtANTFyX61MWLpQU3VormBm7KNk3if5ILUCx3IJ/vduHMG1uUNw20Z2L3wAe+ynsm2sfjybPlPRPDcJXV0qXCORwQdoUUWMRyREkyHMSUHDdUCn6jY2bkuD7HItY2d0aLmGKKihQz8HnZwKqqdneK1gcJiEPZFUW95TVcJRnAuiirDdoHFHpOoPe4vFwbRPIjFPZRUHBkfkqwWSP8BTkwSzXMBMC0AAAAAElFTkSuQmCC',
      'aircraft-icon-light':
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACoAAAAsCAYAAAATmipGAAAACXBIWXMAABYlAAAWJQFJUiTwAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAALxSURBVHgB7ZlNSypRGMeP10sQVGgUVPQyULQJIoKCaNG0axHc+Qa3lq1u36D6BNXqLrVV22nhRoU7gi8LUdyJijAquvCN8QVBEb3PExYmOs7LEYrmB8eBmXP0z/Ocx+c/M4R8V7rdrgUHoYyJUALEMT6fz5ZOp9mpqSliNpvtHMfdmUwmkVDgJ6EEz/M3+Xye7Tt14fV6GTieEgr8IBTAVHc6nYvB85lMhqW1DagIBSzNZnPkNUIBWkInjiGUNoZQ2hhCaWMIpc2XEWomOonH43uBQOBvqVRiBq+1Wi1SqVS4o6OjF4fDIREdaLZ5aDZA4I0oite1Wk127uzsLI7b8/PzR7B9mgRrEhoMBtlkMmmDaDFq1q2trYnb29t3W1tbdqISVULRHAuCcJ9KpTgZtyQLmmqLxaLaVCsW6vf7/0CabyHNtG4zxLOzs8eNjY0HJZPHCsViicVi97lcjiUTALaBCL9xenV1JcrNGylUTbHoRUmxDRWqtVj0IldsH4TSKBa9jCq2d6FqiwW/cG5uToKUPe3v70ecTqdtcItgSg8PDy+z2SxbLpdPJElisAko5EOxvd4uYySfn58fxu1FFLewsCAsLS15YAiQKuFt/ag1m5ubwlsqYd4eBOSiWCye1Ov1PRhyP4fPCe5hDY+RHXtf39vogtVqfTk+Pub1PFCAtRE4XPdEMy6Xi4U6+A1RZseIHv4AAqImQVoj09PTKM6ute2NES3CwY4DRYfDYbZarf6CLcJBxMlIoYuLi9L8/Dy/s7PztLq6GpmEOIWiLdFolC0UClyj0Tgh/UJ7E63kE9ALEN8b7xjGmTaGUNp8H6H4H+h2u23Duhqew2tynUspuh6NK3VZ0DxEaKOXBwcHAtGI5oiGQqGbRCLxT4kVhI7D4FxcQzSiSSimElz/rRpDjXNxjdZtQOVlA7qqlZWVyMzMjGd9ff21o8DbEU6hS1KEZqHoDZaXlwXwB57d3d1hxkXAj37D0W63WWLwSfgP/5+bI3nt9W4AAAAASUVORK5CYII=',
      'aircraft-icon-normal':
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACoAAAAsCAYAAAATmipGAAAACXBIWXMAABYlAAAWJQFJUiTwAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAALASURBVHgB7ZhNaxNBHMafbW1sMIlJaTAtbV0lCipIEPQiku3Ng2CunkyPnuw3aPsJak8ek170IhjBk4rZgNSDKLkVU4qr1VRMsZs01KRps86sseRtsy+ZQCD7g8nC7kzyY2b/m2cWGFQURfHShn6FyPHK9rukknmiKJvPSHseo+fACA6MoGIo/Yo2nPRdFrnJW7NgwBAY8G+pq9GWC8UtgdVtwESU4MVRWfsaA1iJ9hxblDW2KGtsUdbYoqwZHFFlNxOC9DKGyl7rRXou+zaprD/m0SWW05MaNnLvFyBL820l6xlxAyfdi5i5s8JxnAwLWBJVch8E5DdjKOd5M+PgmpbgubjE+YJxmMSUqBqEs+IyCl8jqJZhiWEHmWFvHOcjS2R2JaPDDIsqP9ceoiAtkmVmtc2QMHV7hTt99pGRzrqiarHIn5exnxXQC7xBCYXMLHfpgdSpm6aoqWLpFgPF1lbUcrF0S4diaxBlUizdolFsx6Kmi2WIfKHDI8PhXsWZa2lIr1of+nRJAzfmUPwhoPQ7jLLMo3oAgzQUmyqqzuTG0y+69yKVc46LcAZS8AREzjktdhxPRS/cO/d/Zki/ELbXoijvhFEphkjr/Ht1409AD9rZ4Rbh8L3AxM2EmWdfM2Rsmhzma9I8tl4LOMjfJ7Ms6Em3Fx0dJ0vqSWPYSeXiVv/2dKQlcojTpkrnPhHZwl1yi0RQ2oG2qNMvY3QsAf+VVYxOpXshp0WTtBfyuoA/uQgq+2HUi9Y6+tAH1CYoUWvH2MGZNbYoawZoz0Sfgd/faO+ZyDUWb567euNsOGWNeCT4gnOc/7oIi1ieUSX3cQG7G0lDUbBS4GlfdYxFrG3ujIaYZppCihn0Q4kRaKpyTZJs4ErBN5PAITm39y1iOCUZwLoozQanJkSSD1IYu9ouuIj0oyFwHB0KsOkT/gL9wVHf1omQ/AAAAABJRU5ErkJggg==',
      'aircraft-icon-dark-selected':
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACoAAAAsCAYAAAATmipGAAAACXBIWXMAABYlAAAWJQFJUiTwAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAANUSURBVHgB7ZlPSBRRHMd/Y5urrpD295SMechLYnvKEpoOCp1c6FQJ6qUgiDwU3Wo9RpB2ELzEbmCdgtZTkIIbWN7U6lAezMVOlaGgjrqZ0/c3+0Zn/+/sjraw84UfM+/Ne7Of93vvN/N7O0SlKk3TatioWAU4GTau7SrAdVRsYrD10Qnt15Vb2mLXXU3FOYOTTZLIBompXvrdfY/+/liM3dhTRcdeDfJprSRJy1Sgysge6WvSgGRpa2rctUJlF+ieywG1Ww6o3XJA7ZYDardKBxTv+WYcAubXpyFRN25HFpV3UiISkYfba2rvemiU1oZDKdt5On3kud7Bp37Y03wTlLxAAangEIh++iqvPHlGqbxp1oETR6n6xlVyn/dGUOwDbJAsyhKomMJ+eNG3OvSSNsbeW+lOFW2tuncBHqQYcCTXvjmDAvIODn41NFqjYpq3d9M4S2LvMiygIxRbCgO59MsKKoKlH9Or8DRjuskOuU7V0aEHtxk8guKlbN6VMgDmFCyFKtdgk9JAKmQhWFjlTafh7dm4OlfDSdqa+561by7BJiUAymQhWBiu/JyXKtpbqQx7pJ+Xe+KuH38T0Af5B8tlY2wiaSCJyhRskgkya7Dwhk2Ha/Hy6BmOp+k5bAYWSAUKcaUCu4h7ytEPU7Q5OaVDayl+I12wSQJSxmF++f6jpGBhuMq2Czqcq6GO4cKofgcL4yZhc/80oPWGZ0RgdjM0rJmBNwEe/TxL2wnLi4Pt8GDfTn+X+aIByaNyt5zV4cqbGrkqDBuBhaw8+xKFvuz5XmNw7havAutCUeHfjgJ8Y3Jah976thAPbi6wy90xz/GUzgi4oB378hTQEb43G0PDIQqso/rmNd/W3ELSzJpBl/Go4GeQvub2Ai6dEqBr4CgF5qPYEtkFFQ1rqQgkHBQStiMncbZbDqjdKqk9k0yZ90yB/7pnYhlZFvIDOVV+wK9fT2cHVfnaIyj2GK/cfFTI5o5zVb86PEJq6G3GtpW+dh0YeYIfsH2Uh/Ld3Mk4zBt/hRtZ1cEzjXrKp62qempnzpI4fzgSfMzd6wvJFyyD8peElaEX2ubHL8ZXkGnYAC8HYQOiTm/DbYVk2i8J0CXYa1hvpu9Kom23aLu0r6COMugfGYgAxr9jIicAAAAASUVORK5CYII=',
      'aircraft-icon-medium-selected':
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACoAAAAsCAYAAAATmipGAAAACXBIWXMAABYlAAAWJQFJUiTwAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAMNSURBVHgB7Vg/b9NAHH1GSEygVjDB4rDAWCS6IdXd2NqJDTWIjYX0E6T9BGkHmJAS/kwwNHwAlCDKQocAC0wkSztVSgQTLOY9+xzyx03s5EojxU865Xx3v/O7d3e5dwbmFb7vLyhhVkFyLlPN/4eyyjBrCIh9q/j+Mwr6gpyVJ3FYggMLMFPdxssc8KsVFl5g0cO2couO43QwJc7BDsI1GZEUfnf666aELaKnjoyobWREbSMjahsZUduYH6I855f4U+47PiP8DMpqNlzUxESN7yzxTG/gYNsLDMkgXrHsYNtlrsm2xWl86kTuiS/0IBWP6i7ePUCsmr246AJ3SkBuXQ236aYqSIlURM0USsV1fNwEvleQCjfzwO0icMmtICTcShqamChJPubPFr7uLuBgq9fGpYPUXd4i6Y0Wn3ZJdidJ2FiiZrOUOL1eMM1HdVjBFXZ7d0/qtvi0Ok5dZwRBLfwilStQRQQqngak7nJROWYChWOnyjmBpIc0m0W46g2rLdWOP4+PTbDZnAGCjEixWUTu+hpwIx/ekZ4OjPuRHw7ysM6+no9fNiM2m9NDcvxmERmRy61p9HpWIzKAZCvHEgU4JWAQVtini2aV/6pvQ9Jx7zhhszmGJGvRRHV1eNQiJ8Wk3OUlPavBe6Y6O6n3xccTzUXKmI2ZD0gDS13SUnxweWnZ3Gt048/3VUYkNSopJnJSkKSY2COqaf77BsFYKV/oDi637jFtQIrr3T/4CpEX6bi1bb5y+P4nnnLHDeXa5qtHIcmx141/gv4Uwk0Yn2faCyLE4UupL7536qUzhxOuuTQfDZJOfcK+JIzHxCkNlshqd+pNR4uYARiBqiZ1kRln28iI2sZc3ZlcjL4zlf2zvDMJxmXV6A88vL413OANy1QXXvA8TIFpLnfyqjXsb7rYL8QbDJWpTm3YNoiZEJNe7lzoJIo+hUeu6tpKaGD+dEKj0euS5B/uNxWem8YvpCYanMIfaAUOa9GZLJOwoyk2aceUhW3UNuHZb5to25iIkcZlwHC0/yvRDCPwFzYXEYGVuyb3AAAAAElFTkSuQmCC',
      'aircraft-icon-light-selected':
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACoAAAAsCAYAAAATmipGAAAACXBIWXMAABYlAAAWJQFJUiTwAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAMaSURBVHgB7Zk/TyJBGMbfvRitTLxoZeNaqJWJ0Uob18L6iJ3VYWN7foPDT+Bda4NXWR6X2CuVNhATaKBxaaCBaAINNNzzLDO4IH+X8c6EfZI3m92d2f3NOzPMM6zItKrZbC4w5KMKcDbitvmqOK+JIVliSATL5XLRh4cHmZ2dle3tbdnY2LizLOtADOiTGJDq6mg6nZZ6vS7ValXu7+95yzE1DIyAQh4MAbUajUbHvUllCvTdFYKaVghqWiGoaYWgpjU9oFjLt3CI+5dPLXXt1oSLCuyelNn4DhNyls1mJZVK9Sy3s7PjBRRD/ISbepEACgQKSAeHeLFYtJPJpPTKpl/z8/Oyu7srtm27OD0H7JWMqbFAVRdeIIsR2rh8Pj9OdVlfX/eyC/AraQG7o9YdGRSQ33CIZTKZBe07g4jZJSygXWkNhR+j1BsKqibLBbrXYTeju8WElpaW5PDwkOAuTg+GZdcaADjSZJlUo042qw+kI2NMFmp5eflNtpm1crk8tO4ok83qArRljMlCODxc1tbWZG5uTi4vLzvun56eeo0slUres4YNm0GTbcYHOXSycHep4VZWVgjHbvqFeETEe70cLz1BOIDYxzPtQqEgrut60L59lSc2ho0CbBTluTFsTzZLQdo4PN3c3LxpNeHYUsItLi4S7g6Xkwhuhe/89XtlFFrVmVETM4rYR2wRWEPXarWOuhw2R0dH7foz/psakmOGGSMcM0goxB9EYpzfvm6hLjN/phsHOYivOHX4bg1O6O6x3QHK8aEyxy59VHBXQZe9IdAun80gNBLiIL7s7e1FKpWKNwT6gb4ANCFqzL0HXD91QS8gUQ4iIq0h8gqqCn6WDyCVoISKtkLjbFohqGlN1Z7JlsF7pvh/3TNR2mXBH9i9/AGXXy4im5ubLk5P9JIbRJNs7uhVYwQE6MCyAPX+KodPiAH2XAIo6ObOxuHp+vra617tqhi0fHRFXAL9Lon+4fj4mNVXg/iFGZlAXcaF3oCuKkFvCrAIrR3Ot7Th+OdSn2qeEb8RZ4M+KKiyUVX22eQnnVCT6C9gCNSSgl+9ZwAAAABJRU5ErkJggg==',
      'aircraft-icon-normal-selected':
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACoAAAAsCAYAAAATmipGAAAACXBIWXMAABYlAAAWJQFJUiTwAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAMOSURBVHgB7Zmxb9NAFMY/l9JWRamSUlSpQsJFapCQKlUwwYIZmInYmEgXVvIfkP4FhZUlYYGRILE3TEygSkGAyoAZCEIlTdRA1aZqzXu+c1onTmI7Vxop/qSnxPad8/O7e/F3NjCssiwrzoFBFcHpFGvWkXK8D4MmG6z6xbI+5y1r44VlfydwKJIGBZJDXcXXl8B+Xew8MwZcSfO3hKZpNfSpEaiRmJMOJOug4T7Wp1SBnrgiUNWKQFUrAlWtCFS1hgeU7vNL9JFDo95+UOxbU+GiQpsSaUQe42Avg62PwOZ774YXrosAshRPwxqUUKAEaYCzuFPW8eOt24x46WwMmL0BTOkmba0QbB4BFQhUDuEqZTGFX++A2gYCKZ4EZii7Y7E8BLDpt6tvUIJ8BB6+SimOzQ/A4R5CibPLUyGeNCGmwhM/3XqCymJZpcIwUKZh3ilDiSZmgIt3OLsmbd3ulV2tC6C/YulXPotN6wBpIEixsCbn2rPNWdv93buvj2LTWgB1BCkWhotRl8QC/SOPA5+euY9ffSgu8u9Pca5e06ZLsWnHIHsXywgt2M5JuKlLDMfD9JxiHTwCXqDAMoVBcYvOqWP7O1A3CZ6gDxvtv9Gh2DQJqdPHN5hv2q+a4RJJATdxnuGKtJfmA4p0kqKrvzfovJMZWZhpGxpYsoEd6P0/7r48bS7fa/YfdR10IPmqOGMMx8NLUBSvKQpB/vtaRX0585nmxcV0g+IBOOP82wy9bQrolrntBuWUi8zxkK5LuLyKdbkHtMnn5rChJ+cMiruYvZnCbkXM6w6gNQItQM65k4DrpBboOCXKoEhBTJEjUNkwgQGQTFBBRlORcVatCFS1hmrNpKP7mil3qmsmVtNlVUq6pz/gh7l8E5leNGlr2bnlhlE/izv2qlkbcKvUvfH0IgFfI/DxLMGuIITCLu50sAlxHoU7rop9AVs+ftrMt8DjLon9w8J97j4fxi+Moh+5jQt7A3ZVBdubxmMpsmrCJTmG439LvqqpUryiyHR7ryTbpmXb6kC+0hlK/QNjP4PB5XEBtwAAAABJRU5ErkJggg=='
    };
    Object.keys(base64IconFromInternet).forEach((key) => {
      window.map.loadImage(base64IconFromInternet[key], (error, image) => {
        if (error) throw error;
        window.map.addImage(key, image as any);
      });
    });
    window.map.on('load', () => {
      // Traffic source
      window.map.addSource('traffic-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
      window.map.addLayer({
        id: 'traffic-symbol',
        type: 'symbol',
        minzoom: 5,
        source: 'traffic-source',
        layout: {
          'icon-image': 'aircraft-icon',
          'icon-rotate': ['get', 'heading'],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-size': 0.7
        },
        paint: {
          'icon-opacity': 0.5
        }
      });
      window.map.addSource('traffic-hover-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
      window.map.addLayer(
        {
          id: 'traffic-hover-symbol',
          type: 'symbol',
          minzoom: 5,
          source: 'traffic-hover-source',
          layout: {
            'icon-image': 'aircraft-hover-icon',
            'icon-rotate': ['get', 'heading'],
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'icon-size': 0.7
          },
          paint: {
            'icon-opacity': 1
          }
        },
        'traffic-symbol'
      );
      // End traffic source
      // Traffic track
      window.map.addSource('current-track-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
      window.map.addLayer(
        {
          id: 'current-track-line',
          type: 'line',
          source: 'current-track-source',
          layout: {
            'line-cap': 'round',
            'line-join': 'round'
          },
          paint: {
            'line-color': '#0051FB',
            'line-width': 4,
            'line-opacity': 1
          }
        },
        'traffic-symbol'
      );
      window.map.addSource('first-track-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
      window.map.addLayer(
        {
          id: 'first-track-line',
          type: 'line',
          source: 'first-track-source',
          layout: {
            'line-cap': 'round',
            'line-join': 'round'
          },
          paint: {
            // 'line-color': '#7F4BFE',
            'line-color': '#b3c9f9',
            'line-width': 2,
            'line-opacity': 1,
            'line-dasharray': [10, 2] // blue
          }
        },
        'traffic-symbol'
      );
      window.map.addSource('second-track-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
      window.map.addLayer({
        id: 'second-track-line',
        type: 'line',
        source: 'second-track-source',
        layout: {
          'line-cap': 'round',
          'line-join': 'round'
        },
        paint: {
          'line-color': '#E06AC0',
          'line-width': 2,
          'line-opacity': 1,
          'line-dasharray': [10, 4] // pink
        }
      });
      window.map.addSource('third-track-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
      window.map.addLayer({
        id: 'third-track-line',
        type: 'line',
        source: 'third-track-source',
        layout: {
          'line-cap': 'round',
          'line-join': 'round'
        },
        paint: {
          'line-color': '#EF7438',
          'line-width': 2,
          'line-opacity': 1,
          'line-dasharray': [10, 6] // orange
        }
      });
      window.map.addSource('traffic-selected-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
      window.map.addLayer({
        id: 'traffic-selected-symbol',
        type: 'symbol',
        source: 'traffic-selected-source',
        layout: {
          'icon-image': 'aircraft-focus-icon',
          'icon-rotate': ['get', 'heading'],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-size': 0.9
        },
        paint: {
          'icon-opacity': 1
        }
      });
      window.map.addSource('sector-airport-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
      window.map.addLayer({
        id: 'sector-airport-symbol',
        type: 'symbol',
        source: 'sector-airport-source',
        layout: {
          'text-field': '{icon}',
          'text-size': 20
        },
        paint: {
          'text-color': '#C4B5FD'
        }
      });
      window.map.addSource('sector-route-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
      window.map.addLayer({
        id: 'sector-route-line',
        type: 'line',
        source: 'sector-route-source',
        layout: {
          'line-cap': 'round',
          'line-join': 'round'
        },
        paint: {
          'line-color': '#C4B5FD',
          'line-width': 2,
          'line-opacity': 1
        }
      });
      // End traffic source
      // Waypoint source
      iconAndTextLayerWaypoint('waypoint', 'waypoint-icon', 0, 13, 0.3);
      // Fir source
      window.map.addSource('fir-layer-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
      window.map.addLayer({
        id: 'fir-layer',
        type: 'line',
        source: 'fir-layer-source',
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
          visibility: 'none'
        },
        paint: {
          'line-color': ['get', 'borderColor'],
          'line-width': 4,
          'line-opacity': 0.15
        }
      });
      window.map.addLayer({
        id: 'fir-fill',
        type: 'fill',
        source: 'fir-layer-source',
        layout: {
          visibility: 'none'
        },
        paint: {
          'fill-color': ['get', 'color'],
          'fill-opacity': 0.05
        }
      });
      window.map.addSource('fir-layer-selected-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
      window.map.addLayer({
        id: 'fir-layer-selected',
        type: 'line',
        source: 'fir-layer-selected-source',
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
          visibility: 'none'
        },
        paint: {
          'line-color': ['get', 'borderColor'],
          'line-width': 4,
          'line-opacity': 0.7
        }
      });
      window.map.addLayer({
        id: 'fir-fill-selected',
        type: 'fill',
        source: 'fir-layer-selected-source',
        layout: {
          visibility: 'none'
        },
        paint: {
          'fill-color': ['get', 'color'],
          'fill-opacity': 0.2
        }
      });
      window.map.addSource('airport-icon-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
      window.map.addLayer({
        id: 'airport-symbol',
        type: 'symbol',
        source: 'airport-icon-source',
        minzoom: 5,
        layout: {
          'icon-image': 'airport-icon-{colour}',
          'icon-size': ['get', 'size'],
          'text-field': ['get', 'id'],
          'text-anchor': 'left',
          'text-offset': [0.6, 0],
          'text-size': 11,
          'text-font': ['Inter Medium']
        },
        paint: {
          'text-color': ['get', 'textColor'],
          'text-halo-color': '#000000',
          'text-halo-width': 0.2
        }
      });
      iconAndTextLayerVisibility('fir', 'fir', 0, 13, 0.3);
      fillLayerWithBorder('notam-fir');
      iconLayer('notam-fir', 'fir-notam-icon', 5);
      fillLayerWithBorder('notam-fir-bookmarked');
      iconLayer('notam-fir-bookmarked', 'fir-notam-bookmarked-icon', 5);
      iconLayer('notam', 'notam-icon', 5);
      iconLayer('notam-bookmarked', 'notam-bookmarked-icon', 5);
      const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 25
      });
      popupSelected = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 25
      });
      window.map.on('mousemove', 'traffic-symbol', (e) => {
        if (
          e.features &&
          e.features.length > 0 &&
          hoveredAircraftId !== e.features[0]?.properties?.id
        ) {
          const feature = e.features[0];
          const id = feature?.properties?.id;
          const coordinates = (feature.geometry as any).coordinates.slice();
          hoveredAircraftId = id as string;
          popup
            .setLngLat(coordinates)
            .addClassName('popup-atc')
            .setText(id)
            .addTo(window.map);
          setSourceData('traffic-hover-source', {
            type: 'FeatureCollection',
            features: [e.features[0]]
          });
        }
      });
      window.map.on('mouseleave', 'traffic-symbol', () => {
        if (hoveredAircraftId !== null) {
          if (popup) {
            popup.remove();
          }
          setSourceData('traffic-hover-source', {
            type: 'FeatureCollection',
            features: []
          });
        }
        hoveredAircraftId = null;
      });
      window.map.on('click', 'traffic-symbol', (e) => {
        selectedAircraftId = null;
        if (e.features && e.features.length > 0) {
          const properties = e?.features?.[0]?.properties;
          dispatch(
            setSelectedRadar({
              ...(properties as any)
            })
          );
        }
      });
      window.map.on('click', (e) => {
        // check if e is doesn't have feature
        if (selectedAircraftId) {
          selectedAircraftId = null;
          dispatch(setSelectedRadar(null));
          if (popupSelected) {
            popupSelected.remove();
          }
          if (popup) {
            popup.remove();
          }
          clearSelectTrack();
        }
      });
      window.map.on('moveend', (e: any) => {
        const bound = window.map.getBounds();
        const mapNodes = geoData.filter((item: any) => {
          const lat = item?.geometry?.coordinates[1];
          const lng = item?.geometry?.coordinates[0];
          return (
            lat >= (bound?._sw?.lat ?? 0) &&
            lat <= (bound?._ne?.lat ?? 0) &&
            lng >= (bound?._sw?.lng ?? 0) &&
            lng <= (bound?._ne?.lng ?? 0)
          );
        });
        window.updateData({
          event: 'airport',
          data: mapNodes
        });
      });
      setIsMapReady(true);
    });
    function fillLayerWithBorder(
      id: string,
      linePaint = {
        'line-color': ['get', 'borderColor'],
        'line-width': 4,
        'line-opacity': ['get', 'borderOpacity']
      },
      fillPaint = {
        'fill-color': ['get', 'color'],
        'fill-opacity': ['get', 'colorOpacity']
      }
    ) {
      window.map.addSource(id + '-layer-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
      window.map.addLayer({
        id: id + '-line',
        type: 'line',
        source: id + '-layer-source',
        layout: {
          'line-cap': 'round',
          'line-join': 'round'
        },
        minzoom: 5,
        paint: linePaint as any
      });
      window.map.addLayer({
        id: id + '-fill',
        type: 'fill',
        minzoom: 5,
        source: id + '-layer-source',
        paint: fillPaint as any
      });
    }
    function iconAndTextLayerVisibility(
      id: string,
      icon: string,
      minzoom = 0,
      textSize = 13,
      haloWidth = 0.3
    ) {
      window.map.addSource(id + '-icon-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
      window.map.addLayer({
        id: id + '-symbol',
        type: 'symbol',
        source: id + '-icon-source',
        minzoom,
        layout: {
          'icon-image': icon,
          'icon-allow-overlap': true,
          'text-allow-overlap': true,
          'icon-ignore-placement': true,
          'text-ignore-placement': true,
          'text-field': ['get', 'id'],
          'text-anchor': 'left',
          'text-offset': [0.5, 0],
          'text-size': textSize,
          'icon-size': 0.2,
          'text-font': ['Inter Medium']
        },
        paint: {
          'text-color': ['get', 'textColor'],
          'text-halo-color': '#000000',
          'text-halo-width': haloWidth
        }
      });
    }
    function iconLayer(
      id: string,
      icon: string,
      minzoom = 0,
      textSize = 13,
      haloWidth = 0.3
    ) {
      window.map.addSource(id + '-icon-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
      window.map.addLayer({
        id: id + '-symbol',
        type: 'symbol',
        source: id + '-icon-source',
        minzoom,
        layout: {
          'icon-image': icon,
          'icon-size': 0.2,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true
        }
      });
    }
    function iconAndTextLayerWaypoint(
      id: string,
      icon: string,
      minzoom = 0,
      textSize = 13,
      haloWidth = 0.3
    ) {
      window.map.addSource(id + '-icon-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
      window.map.addLayer({
        id: id + '-symbol',
        type: 'symbol',
        source: id + '-icon-source',
        layout: {
          'icon-image': icon,
          'text-field': ['get', 'id'],
          'text-anchor': 'left',
          'text-offset': [0.8, 0],
          'text-size': textSize,
          'icon-size': 0.2,
          'text-font': ['Inter Medium']
        },
        paint: {
          'text-color': ['get', 'textColor'],
          'text-halo-color': '#000000',
          'text-halo-width': haloWidth
        }
      });
    }
    window.updateData = (e: any) => {
      switch (e.event) {
        case 'zoom-in':
          window.map.zoomIn();
          break;
        case 'zoom-out':
          window.map.zoomOut();
          break;
        case 'camera':
          window.map.flyTo({
            center: e.data.center,
            zoom: e.data.zoom,
            duration: 2000
          });
          break;
        case 'route':
          setSourceData('route-source', {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: e.data
            }
          });
          break;
        case 'traffic':
          setSourceData('traffic-source', {
            type: 'FeatureCollection',
            features: e.data
          });
          break;
        case 'trafficTrack':
          setSourceData('traffic-track-source', {
            type: 'FeatureCollection',
            features: e.data
          });
          break;
        case 'waypoint':
          setSourceData('waypoint-icon-source', {
            type: 'FeatureCollection',
            features: e.data
          });
          break;
        case 'toggleWeather':
          toggleLayer('weather-layer', e.data);
          break;
        case 'toggleAirport':
          toggleLayer('airport-symbol', e.data);
          break;
        case 'toggleTraffic':
          toggleLayer('traffic-symbol', e.data);
          break;
        case 'airport':
          setSourceData('airport-icon-source', {
            type: 'FeatureCollection',
            features: e.data
          });
          break;
        case 'trafficSelected':
          setSourceData('traffic-selected-source', {
            type: 'FeatureCollection',
            features: e.data
          });
          break;
        case 'popupSelected':
          if (popupSelected) {
            popupSelected.remove();
          }
          popupSelected = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            offset: 25
          });
          popupSelected
            .setLngLat(e.data.coordinates)
            .addClassName('popup-atc')
            .setText(e.data.callsign)
            .addTo(window.map);
          break;
        case 'popupCurrentSelected':
          if (popupCurrentCII) {
            popupCurrentCII.setLngLat(e.data.coordinates);
          }
          break;
        case 'trafficCurrent':
          if (e.data?.properties?.id) {
            setSourceData('traffic-current-source', {
              type: 'FeatureCollection',
              features: [e.data]
            });
          } else {
            setSourceData('traffic-current-source', {
              type: 'FeatureCollection',
              features: []
            });
          }
          break;
        case 'currentTrack':
          setSourceData('current-track-source', e.data);
          break;
        case 'firstTrack':
          setSourceData('first-track-source', e.data);
          break;
        case 'secondTrack':
          setSourceData('second-track-source', e.data);
          break;
        case 'thirdTrack':
          setSourceData('third-track-source', e.data);
          break;
        case 'firstTrackToggle':
          // toggleLayer('first-track-line', e.data);
          break;
        case 'secondTrackToggle':
          // toggleLayer('second-track-line', e.data);
          break;
        case 'thirdTrackToggle':
          // toggleLayer('third-track-line', e.data);
          break;
        case 'aircraft-selected':
          setSourceData('aircraft-icon-selected-source', {
            type: 'FeatureCollection',
            features: e.data
          });
          break;
        case 'aircraft':
          setSourceData('aircraft-icon-source', {
            type: 'FeatureCollection',
            features: e.data
          });
          break;
        case 'hideTraffic':
          window.map.setPaintProperty('traffic-symbol', 'icon-opacity', 0.1);
          break;
        case 'showTraffic':
          window.map.setPaintProperty('traffic-symbol', 'icon-opacity', 0.5);
          break;
        case 'firLayer':
          setSourceData('fir-layer-source', {
            type: 'FeatureCollection',
            features: e.data
          });
          break;
        case 'firIcon':
          setSourceData('fir-icon-source', {
            type: 'FeatureCollection',
            features: e.data
          });
          break;
        case 'toggleFir':
          toggleLayer('fir-layer', e.data);
          toggleLayer('fir-fill', e.data);
          toggleLayer('fir-symbol', e.data);
          toggleLayer('fir-layer-selected', e.data);
          toggleLayer('fir-fill-selected', e.data);
          break;
        case 'toggleNotam':
          toggleLayer('notam-fir-bookmarked-symbol', e.data);
          toggleLayer('notam-fir-symbol', e.data);
          toggleLayer('notam-bookmarked-symbol', e.data);
          toggleLayer('notam-symbol', e.data);
          toggleLayer('notam-fir-fill', e.data);
          toggleLayer('notam-fir-line', e.data);
          break;
        case 'notamFir':
          setSourceData('notam-fir-layer-source', {
            type: 'FeatureCollection',
            features: e.data
          });
          break;
        case 'notamFirText':
          setSourceData('notam-fir-icon-source', {
            type: 'FeatureCollection',
            features: e.data
          });
          break;
        case 'notamFirBookmarked':
          setSourceData('notam-fir-bookmarked-layer-source', {
            type: 'FeatureCollection',
            features: e.data
          });
          break;
        case 'notamFirTextBookmarked':
          setSourceData('notam-fir-bookmarked-icon-source', {
            type: 'FeatureCollection',
            features: e.data
          });
          break;
        case 'notam':
          setSourceData('notam-icon-source', {
            type: 'FeatureCollection',
            features: e.data
          });
          break;
        case 'notamBookmarked':
          setSourceData('notam-bookmarked-icon-source', {
            type: 'FeatureCollection',
            features: e.data
          });
          break;
        case 'sector':
          setSourceData('sector-airport-source', e.data);
          break;
        case 'sectorRoute':
          setSourceData('sector-route-source', e.data);
          break;
        default:
          break;
      }
    };
    window.initWeather = (url: string) => {
      const source = window.map.getSource('weather-raster');
      if (source) {
        window.map.removeLayer('weather-layer');
        window.map.removeSource('weather-raster');
      }
      window.map.addSource('weather-raster', {
        type: 'raster',
        tiles: [
          'https://api.accuweather.com/maps/v1/radar/globalSIR/zxy/{z}/{x}/{y}.png?apikey=de13920f574d420984d3080b1fa6132b'
        ],
        tileSize: 256,
        maxzoom: 12
      });
      window.map.addLayer({
        id: 'weather-layer',
        type: 'raster',
        source: 'weather-raster',
        paint: {
          'raster-fade-duration': 0,
          'raster-opacity': 1
        },
        maxzoom: 12
      });
    };
    function setSourceData(id: string, data: any) {
      const source: any = window.map.getSource(id);
      if (source) {
        source.setData(data);
      }
    }
    function toggleLayer(key: string, show: string) {
      if (show === 'none') {
        window.map.setLayoutProperty(key, 'visibility', 'none');
      } else {
        window.map.setLayoutProperty(key, 'visibility', 'visible');
      }
    }
  }, []);
  return (
    <div className="w-full relative">
      <RouteLayer />
      <div id="map" className="w-full min-h-screen" />
      <NotamLayer />
    </div>
  );
};

export default ATCMap;
